import { useEffect, useRef } from 'react';
import { Phone, PhoneOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CallPeer, CallSession } from '@/types/call';

interface CallIncomingModalProps {
  session: CallSession;
  onAccept: () => void;
  onDecline: () => void;
}

let ringtoneAudio: HTMLAudioElement | null = null;
let vibrateInterval: number | null = null;

export function CallIncomingModal({ session, onAccept, onDecline }: CallIncomingModalProps) {
  const isVideo = session.kind === 'video';
  const displayName = session.peer.display_name || session.peer.username || 'Unknown';
  const username = session.peer.username;

  // Stop any existing ringtone before starting a new one
  const stopRingtone = () => {
    if (ringtoneAudio) {
      ringtoneAudio.pause();
      ringtoneAudio.src = '';
      ringtoneAudio.load();
      ringtoneAudio = null;
    }
    if (vibrateInterval) {
      window.clearInterval(vibrateInterval);
      vibrateInterval = null;
    }
    navigator.vibrate?.([]);
  };
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Play the ringtone when the incoming call modal mounts, and stop it when
  // the modal unmounts (call answered, declined, or ended).
  useEffect(() => {
    stopRingtone();

    const audio = new Audio('/audio/ringtone.mp3');
    audio.loop = true;
    audio.volume = 0.7;
    ringtoneAudio = audio;

    void audio.play().catch(() => {
      // Autoplay blocked; user needs to click the modal to hear sound.
    });

    navigator.vibrate?.([1000, 1000]);
    vibrateInterval = window.setInterval(() => {
      navigator.vibrate?.([1000, 1000]);
    }, 2000);

    return () => {
      stopRingtone();
    };
  }, []);

  // Stop ringtone when leaving the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopRingtone();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      stopRingtone();
    };
  }, []);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] flex items-start justify-center pt-2 sm:pt-4 pb-2"
      role="dialog"
      aria-modal="true"
      aria-label={`Incoming ${isVideo ? 'video' : 'voice'} call`}
    >
      <div className="relative w-full max-w-sm flex flex-col">
        {/* Backdrop blur behind the modal */}
        <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-md rounded-xl border border-white/10 shadow-xl" />

        <div className="relative z-10 flex flex-col items-center justify-center gap-3 px-4 py-4 rounded-xl bg-neutral-900 border border-white/10 shadow-xl">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/50">
            Incoming {isVideo ? 'video' : 'voice'} call
          </p>

          <div className="relative flex items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-white/10" />
            <div
              className={cn(
                'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10 ring-1 ring-white/20',
                'h-14 w-14 text-xl',
              )}
            >
              {session.peer.avatar_url ? (
                <img src={session.peer.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="font-semibold text-white/80">
                  {displayName.trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
            </div>
          </div>

          <h2 className="max-w-full truncate text-lg font-semibold text-white">
            {displayName}
          </h2>
          {username && (
            <p className="text-sm text-white/50">@{username}</p>
          )}

          <div className="mt-4 flex items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDecline();
                }}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                aria-label="Decline call"
              >
                <PhoneOff size={20} />
              </button>
              <span className="text-[10px] text-white/60">Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept();
                }}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md transition hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                aria-label="Accept call"
              >
                <Phone size={20} />
              </button>
              <span className="text-[10px] text-white/60">Answer</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
